import React from 'react'
import { Link } from 'react-router'
import { pluralize } from 'humanize-plus'
import IconSVG from 'svg-inline-react'
import cx from 'classnames'
import { Div } from '@driftt/tide-core-legacy'

import flagIconSrc from '!svg-inline?removeTags=true!images/svg/flag-long.svg'
import userOutlineIconSrc from '!svg-inline?removeTags=true!images/svg/user-outline.svg'
import checkCircleIconSrc from '!svg-inline?removeTags=true!images/svg/check-circle.svg'
import botIconSrc from '!svg-inline?removeTags=true!images/svg/bot.svg'
import welcomeMessageIconSrc from '!svg-inline?removeTags=true!images/svg/welcome-message.svg'
import emailIconSrc from '!svg-inline?removeTags=true!images/svg/email.svg'

import './style'

/**
 * WARNING: this file is a time suck, please double and triple check your logic when changing conditionals here
 *
 * TODO: we should rewrite this and re-evaluate the logic to be clearer
 */

const shouldNotShowSegmentInclusion = (
  isDriftLinkOnly,
  hasOnlyExclusionTargeting,
  hasSegmentDisplayTargeting,
) => {
  return (
    isDriftLinkOnly ||
    hasOnlyExclusionTargeting ||
    hasSegmentDisplayTargeting
  )
}

const Group = ({ iconSrc, to, title, children, iconClassName }) => (
  <Div className="nav-group">
    <Link to={to} className={cx('icon-container', iconClassName)}>
      <IconSVG src={iconSrc} />
    </Link>
    <strong>{title}</strong>
    {children}
  </Div>
)

const ActiveLink = props => <Link {...props} activeClassName="active" />

const MessageGroup = ({ baseUrl, isMessageTypePlaybook, viewConfig: { messageUrlPath, label, previewUrlPath }}) =>
  isMessageTypePlaybook && (
    <Group
      to={`${baseUrl}/message/${messageUrlPath}`}
      title="Message"
      iconSrc={welcomeMessageIconSrc}
      iconClassName="action-group"
    >
      <ActiveLink to={`${baseUrl}/message/${messageUrlPath}`}>{label}</ActiveLink>
      {previewUrlPath && <ActiveLink to={`${baseUrl}/message/${previewUrlPath}`}>{'Preview & Template'}</ActiveLink>}
    </Group>
  )

const SequenceGroup = ({ baseUrl, isSequencePlaybook, isMarketingSequencePlaybook, isOneTimeMarketingSequencePlaybook }) =>
  isSequencePlaybook && (
    <Group to={`${baseUrl}/sequence`} title={isMarketingSequencePlaybook ? "Email" : "Sequence"} iconSrc={emailIconSrc} iconClassName="sequence-group">
      <ActiveLink to={`${baseUrl}/sequence`}>Compose your email{ isOneTimeMarketingSequencePlaybook ? '' : 's' }</ActiveLink>
      <ActiveLink to={`${baseUrl}/sequence/preview`}>Preview</ActiveLink>
      <ActiveLink to={`${baseUrl}/sequence/options`}>Advanced options</ActiveLink>
    </Group>
  )

const GoalsGroup = ({ baseUrl, goals, onAddGoalClick, classificationsByGoalId, isMessageTypePlaybook }) =>
  !goals.isEmpty() && (
    <Group title="Goals" iconSrc={flagIconSrc} iconClassName="goals-group" to={`${baseUrl}/goal/${goals.first().id}`}>
      {goals.toArray().map((goal, index) => (
        <ActiveLink key={`link-${goal.id}`} to={`${baseUrl}/goal/${goal.id}`}>
          {classificationsByGoalId.get(goal.id, `Goal ${index + 1}`)}
        </ActiveLink>
      ))}
      {!isMessageTypePlaybook && (
        <Div className="add-goal-link" onClick={onAddGoalClick}>
          +add new goal
        </Div>
      )}
    </Group>
  )

const LeadBotGroup = ({ baseUrl, numQuestions, isLeadBot }) =>
  isLeadBot && (
    <Group to={`${baseUrl}/leadbot`} title="LeadBot" iconSrc={botIconSrc} iconClassName="action-group">
      <ActiveLink to={`${baseUrl}/leadbot`}>
        {numQuestions} {pluralize(numQuestions, 'question')}
      </ActiveLink>
    </Group>
  )

const TargetingDriftLink = ({
  driftLink,
  baseUrl,
  hasDriftLink,
  hasOnlyExclusionTargeting,
  isSignaturePlaybook,
}) => {
  if (!hasDriftLink || hasOnlyExclusionTargeting || isSignaturePlaybook) {
    return null
  }
  return (
    <ActiveLink key="playbook-nav-targeting-driftlink" to={`${baseUrl}/targeting/driftlink`}>
      #DriftLink{!!driftLink && ` - #${driftLink}`}
    </ActiveLink>
  )
}

const TargetingSegmentInclude = ({
  isDriftLinkOnly,
  hasOnlyExclusionTargeting,
  hasSegmentDisplayTargeting,
  audiencePreview,
  audienceInclusionPreview,
  baseUrl,
  isSignaturePlaybook,
  isOnFreePlan,
}) => {
  if (shouldNotShowSegmentInclusion(isDriftLinkOnly, hasOnlyExclusionTargeting, hasSegmentDisplayTargeting) ||
      isSignaturePlaybook) {
    return null
  }

  return (
    <ActiveLink key={`playbook-nav-targeting-${audiencePreview}`} to={!isOnFreePlan && `${baseUrl}/targeting/audience`} className={`${isOnFreePlan ? 'playbook-nav-targeting-link-disabled' : ''}`}>
      Audience - {audienceInclusionPreview}
    </ActiveLink>
  )
}

const TargetingSegmentExclude = ({
  audiencePreview,
  baseUrl,
  excludedUserListIds,
  userListIds,
  hasOnlyExclusionTargeting,
  isSignaturePlaybook,
}) => {
  if (!hasOnlyExclusionTargeting || isSignaturePlaybook) {
    return null
  }
  return (
    <ActiveLink key={`playbook-nav-targeting-exclusion-${audiencePreview}`} to={`${baseUrl}/targeting/exclude`}>
      Exclusion{excludedUserListIds.size > 0
        ? ` - ${excludedUserListIds.size} ${pluralize(userListIds.size, 'Segment')}`
        : ''}
    </ActiveLink>
  )
}

const TargetingDisplayConditions = ({
  isDriftLinkOnly,
  isEmailPlaybook,
  hasOnlyExclusionTargeting,
  resetTargetingView,
  baseUrl,
  targetingConditionsCount,
  renderDisplayPreview,
  isSignaturePlaybook,
}) => {
  if (
    isDriftLinkOnly ||
    isEmailPlaybook ||
    hasOnlyExclusionTargeting ||
    isSignaturePlaybook
  ) {
    return null
  }

  return (
    <ActiveLink
      key={`playbook-nav-targeting-display-${renderDisplayPreview()}`}
      onClick={resetTargetingView}
      to={`${baseUrl}/targeting/${targetingConditionsCount > 0 ? 'displaywhen' : 'display'}`}
    >
      Display - {renderDisplayPreview()}
    </ActiveLink>
  )
}

const TargetingAdvanced = ({
  isDriftLinkOnly,
  hasOnlyExclusionTargeting,
  isEmailPlaybook,
  baseUrl,
  isSignaturePlaybook,
}) => {
  if (
    isDriftLinkOnly ||
    hasOnlyExclusionTargeting ||
    isEmailPlaybook ||
    isSignaturePlaybook
  ) {
    return null
  }
  return (
    <ActiveLink
      className="playbook-nav-targeting-advanced"
      key="playbook-nav-targeting-advanced"
      to={`${baseUrl}/targeting/advanced`}
    >
      Frequency
    </ActiveLink>
  )
}

const TargetingGroup = (props) =>
  !props.isSignaturePlaybook &&
    <Group
      title={props.targetingGroupTitle}
      iconSrc={userOutlineIconSrc}
      iconClassName="targeting-group"
      to={`${props.baseUrl}/targeting/${props.targetingIconLink}`}
    >
      {TargetingDriftLink(props)}
      {TargetingSegmentInclude(props)}
      {TargetingSegmentExclude(props)}
      {TargetingDisplayConditions(props)}
      {TargetingAdvanced(props)}
    </Group>

const ReviewGroup = ({ reviewText, baseUrl }) =>
  <Group title="Review" iconSrc={checkCircleIconSrc} iconClassName="review-group" to={`${baseUrl}/review`}>
    <ActiveLink key="playbook-nav-enable" to={`${baseUrl}/review`}>{reviewText}</ActiveLink>
  </Group>

const SignatureGroup = ({ isSignaturePlaybook, baseUrl }) =>
  !!isSignaturePlaybook &&
    <Group to={`${baseUrl}/message/signature`} title="Signature" iconSrc={emailIconSrc} iconClassName="sequence-group">
      <ActiveLink to={`${baseUrl}/message/signature`}>Compose template</ActiveLink>
      <ActiveLink to={`${baseUrl}/message/signature-preview`}>Preview team signatures</ActiveLink>
    </Group>

const Nav = props => (
  <nav>
    <SignatureGroup {...props} />
    <MessageGroup {...props} />
    <SequenceGroup {...props} />
    <LeadBotGroup {...props} />
    <GoalsGroup {...props} />
    <TargetingGroup {...props} />
    <ReviewGroup {...props} />
  </nav>
)

export default Nav



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/Nav/component.js